import { useRef, useEffect, useState } from 'react';
import { Alert } from '@piggytechng/component-library';
// import parse from 'html-react-parser';
import { InputProps, QueryParam, ReplaceKey } from '../../types';
import { SelectProps } from '@piggytechng/component-library/dist/types';

const Colors = {
  primary: '#1734ca',
};

const isValidEmail = (email: any) => {
  const re =
    // eslint-disable-next-line no-useless-escape
    /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(String(email).toLowerCase());
};

const isValidBriefDescription = (briefDescription: string) => {
  return /^.{5,}$/.test(briefDescription);
};

const isValidPhoneNumber = (phone: string) => {
  return /^(\+234)?(0)?[\d]{10}|(234)?(0)?[\d]{10}|[0][\d]{10}$/.test(phone);
};

const isValidName = (name: string) => {
  return /^[a-zA-Z]{2,}$/.test(name);
};

function useOnClickOutsideRef(callback: Function, initialValue = null) {
  const elementRef = useRef(initialValue) as any;
  useEffect(() => {
    function handler(event: any) {
      if (!elementRef.current?.contains(event.target)) {
        callback();
      }
    }
    window.addEventListener('click', handler);
    return () => window.removeEventListener('click', handler);
  }, [callback]);
  return elementRef;
}

const capitalize = (val: string) => {
  if (val) {
    const value = val.charAt(0).toUpperCase() + val.slice(1);
    return value;
  }
  return '';
};

const OverideButtonStyle = {
  borderRadius: '10px',
  color: Colors.primary,
  style: {
    fontFamily: 'Jakarta',
    fontSize: '14px',
    fontWeight: 700,
    lineHeight: '23.44px',
    letterSpacing: '0.01em',
  },
};

const OverideInputStyle: InputProps = {
  borderRadius: '10px',
  labelStyle: {
    color: '#666D7C',
    fontWeight: 600,
    lineHeight: '15px',
    letterSpacing: '1%',
    fontSize: '13px',
    fontStyle: 'normal',
    // fontFamily: "PlusJakartaSans-Regular",
  },
  backgroundColor: '#f1f1f1',
  color: 'var(--input-text-color)',
  outlineColor: '#D0D0D0',
};

const OverideSelectStyle: SelectProps = {
  borderRadius: '8px',
  labelStyle: {
    color: '#01040F',
    fontWeight: 500,
    lineHeight: '125%',
    letterSpacing: '-0.35px',
    fontFamily: 'Jakarta',
    fontSize: '14px',
    fontStyle: 'normal',
  },
  outlineColor: Colors.primary,
  selectedColor: Colors.primary,
  menuBoxShadow: '0px 4px 24px 0px #E0E3EB',
  options: [],
  value: '',
  bgFocus: '#eff6ff',
  borderFocus: '1px solid #1d4ed8',
  fontSize: '14px',
  style: {
    fontSize: '14px',
    zIndex: 1000,
  },
};

const copyToClipboard = async (nameCopied: string, text: string | number) => {
  try {
    await navigator.clipboard.writeText(`${text}`);
    Alert.info(`${nameCopied} copied to clipboard`);
  } catch (error) {
    Alert.error('Copy failed. Please try again.');
  }
};

const useOutsideClick = (ref: any, fn: Function, actionRef: any) => {
  const handleClickOutside = (event: { target: any }) => {
    if (
      ref.current &&
      !ref.current.contains(event.target) &&
      actionRef.current !== event.target &&
      !actionRef?.current?.contains(event.target)
    ) {
      fn(false);
    }
  };

  useEffect(() => {
    document.addEventListener('click', handleClickOutside, true);
    return () => {
      document.removeEventListener('click', handleClickOutside, true);
    };
  }, []);
};

const convertToNaira = (val: number) => {
  const amount = val / 100;
  const currency = new Intl.NumberFormat('ng-NG', { style: 'currency', currency: 'NGN' }).format(amount);

  return currency.replace('NGN', '₦');
};

const formatToNaira = (val: any) => {
  if (val?.toString()?.trim()?.includes('₦')) return val;

  const currency = new Intl.NumberFormat('ng-NG', { style: 'currency', currency: 'NGN' }).format(val);

  return currency.replace('NGN', '₦');
};

const formatToDollar = (val: number) => {
  const currency = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(val);

  return currency.replace('USD', '$');
};

const NETWORK_LABELS: Record<string, string> = {
  erc20: 'Ethereum (ERC-20)',
  trc20: 'TRON (TRC-20)',
  pol: 'Polygon (POL)',
  bep20: 'BNB Smart Chain (BEP-20)',
};

const formatNetwork = (network?: string) => {
  if (!network) return '';
  return NETWORK_LABELS[network.toLowerCase()] ?? network.toUpperCase();
};

// e.g. ("usdc", "trc20") -> "USDC — TRON (TRC-20)"
const formatCoinNetwork = (currency?: string, network?: string) => {
  const coin = currency ? currency.toUpperCase() : '';
  const net = formatNetwork(network);
  if (coin && net) return `${coin} — ${net}`;
  return coin || net;
};

function formatChildren(children: string) {
  let result = '';

  for (let i = 0; i < children?.length; i++) {
    if (children[i] === '[') {
      result += '<b>';
    } else if (children[i] === ']') {
      result += '</b>';
    } else if (children[i] === '\n' && children[i + 1] === '\n') {
      result += '<br> <br>';
      i++;
    } else if (
      children[i] === '\r' &&
      children[i + 1] === '\n' &&
      children[i + 2] === '\r' &&
      children[i + 3] === '\n'
    ) {
      result += '<br><br>';
      i += 3;
    } else {
      result += children[i];
    }
  }
  return result;
}

const queryParamBuilder = (
  params: QueryParam = {},
  replaceKeys: ReplaceKey[] = [],
  excludeKeys: string[] = [],
): string => {
  if (!params || typeof params !== 'object') return '';

  const queryString = Object.entries(params)
    .filter(([key, value]) => {
      // exclude unwanted keys or empty values
      if (excludeKeys.includes(key)) return false;
      if (value == null || value === '') return false;
      if (Array.isArray(value) && value.length === 0) return false;
      if (typeof value === 'object' && Object.keys(value).length === 0) return false;
      return true;
    })
    .map(([key, value]) => {
      const newKey = replaceKeys.find((r) => r.key === key)?.value || key;

      if (Array.isArray(value)) {
        return `${newKey}=${value.join(',')}`;
      }

      if (typeof value === 'object') {
        return `${newKey}=${Object.entries(value)
          .map(([k, v]) => `${k}=${v}`)
          .join(',')}`;
      }

      return `${newKey}=${value}`;
    })
    .join('&');

  return queryString ? `?${queryString}` : '';
};

function formatBracketText(children: string) {
  let result = '';

  for (let i = 0; i < children?.length; i++) {
    if (children[i] === '[') {
      result += '';
    } else if (children[i] === ']') {
      result += '';
    } else {
      result += children[i];
    }
  }
  return result;
}

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

export {
  isValidEmail,
  isValidBriefDescription,
  isValidPhoneNumber,
  isValidName,
  useOnClickOutsideRef,
  capitalize,
  convertToNaira,
  formatToNaira,
  OverideButtonStyle,
  OverideInputStyle,
  copyToClipboard,
  formatChildren,
  useOutsideClick,
  OverideSelectStyle,
  queryParamBuilder,
  formatBracketText,
  formatToDollar,
  formatNetwork,
  formatCoinNetwork,
  sleep,
  useDebounce,
};
